import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";
import { ALLOWED_MEDIA_TYPES, ALLOWED_DOC_TYPES } from "@/config/constants";
import { IS_MOBILE } from "@/lib/environmentDetector";

export function useDragDrop() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleMediaAttachment } = useMediaMessageSenderState();
  const { handleFileAttachment } = useFileMessageSenderState();

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  if (IS_MOBILE) {
    return { isDragging: false, dragHandlers: {} };
  }

  const onDragEnter = (e: React.DragEvent) => {
    // Only activate for file drags, not text/element drags within the page
    if (!e.dataTransfer.types.includes("Files")) {
      return;
    }
    e.preventDefault();
    dragCounter.current++;
    setIsDragging(true);
  };

  const onDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) {
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) {
      return;
    }
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    if (
      !e.dataTransfer.types.includes("Files") ||
      !e.dataTransfer.files.length
    ) {
      return;
    }
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) {
      return;
    }

    if (ALLOWED_MEDIA_TYPES.includes(file.type)) {
      handleMediaAttachment(file);
    } else if (ALLOWED_DOC_TYPES.includes(file.type)) {
      handleFileAttachment(file);
    } else {
      toast({
        duration: 1000,
        description: t("fileTypeNotSupported"),
        variant: "destructive",
      });
    }
  };

  return {
    isDragging,
    dragHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
